import { cn } from "@ceedero/ui";

import { resolveBoardPatternGeometry } from "@/shelf/utils/board-pattern";
import { resolveBoardPixelsPerMillimeter } from "@/shelf/utils/board-projection";

import type { BoardPattern } from "@/settings/types/types";
import type { BoardBox, ShelfBoardView } from "@/shelf/types/types";
import type { ComponentPropsWithRef, CSSProperties, ReactNode } from "react";

/**
 * The leaves that own the board's inline geometry, and the only ones.
 *
 * A pan offset, a zoom and a rack's position are numbers that change as the
 * board is moved — there is no class for "412.5 millimeters from the left" — so
 * the `style` prop is genuinely necessary here. Encapsulated in these few
 * rather than written out at each call site, which is the rule this repository
 * applies to every other unavoidable inline style.
 */

/**
 * The board itself: one transform, applied once, under which **one unit is one
 * millimeter**.
 *
 * That is what lets everything inside be positioned in the same coordinates the
 * Shelves are stored in, with no per-element scaling to keep in step — and it is
 * why a rack's boards, its bays and the type on its name plate all grow and
 * shrink together, as the parts of one physical object do.
 */
export const BoardSurface = ({
  children,
  className,
  view,
}: {
  children: ReactNode;
  className?: string;
  view: ShelfBoardView;
}) => (
  <div
    className={cn("absolute left-0 top-0 origin-top-left", className)}
    style={{
      transform:
        `translate(${view.panOffsetXPixels}px, ${view.panOffsetYPixels}px) ` +
        `scale(${resolveBoardPixelsPerMillimeter(view.zoomPercentage)})`,
    }}
  >
    {children}
  </div>
);

/** A rectangle of the board, placed in millimeters inside `BoardSurface`. */
export const BoardBoxElement = ({
  box,
  children,
  className,
  ...props
}: {
  box: BoardBox;
  children?: ReactNode;
  className?: string;
} & Omit<ComponentPropsWithRef<"div">, "style" | "className" | "children">) => (
  <div
    className={cn("absolute", className)}
    style={{
      left: `${box.xMillimeters}px`,
      top: `${box.yMillimeters}px`,
      width: `${box.widthMillimeters}px`,
      height: `${box.heightMillimeters}px`,
    }}
    {...props}
  >
    {children}
  </div>
);

/**
 * How much finer than a millimetre `BoardFineBoxElement` lays its box out.
 * A tenth of a millimetre is below anything the board draws at any zoom.
 */
export const BOARD_FINE_UNITS_PER_MILLIMETER = 10;

/**
 * A rectangle of the board placed exactly, for the few boxes whose size is not
 * a whole number of millimetres and must not look it.
 *
 * Chromium rounds a box to whole layout units before the board's scale is
 * applied, and a layout unit here is a millimetre — so a 6.5 mm spine was drawn
 * 6 mm thick, all of the loss landing on one side. This box is laid out at
 * `BOARD_FINE_UNITS_PER_MILLIMETER` units per millimetre and placed and shrunk
 * by a transform, which is never rounded. Its children are laid out in those
 * finer units.
 */
export const BoardFineBoxElement = ({
  box,
  children,
  className,
}: {
  box: BoardBox;
  children?: ReactNode;
  className?: string;
}) => (
  <div
    className={cn("absolute left-0 top-0 origin-top-left", className)}
    style={{
      width: `${box.widthMillimeters * BOARD_FINE_UNITS_PER_MILLIMETER}px`,
      height: `${box.heightMillimeters * BOARD_FINE_UNITS_PER_MILLIMETER}px`,
      transform:
        `translate(${box.xMillimeters}px, ${box.yMillimeters}px) ` +
        `scale(${1 / BOARD_FINE_UNITS_PER_MILLIMETER})`,
    }}
  >
    {children}
  </div>
);

/**
 * A group turned about a point inside itself.
 *
 * The rack's boards, bays and discs turn together here, as the one object they
 * are. The name is deliberately **not** inside this: a label lying on its side
 * is a label nobody reads, so it is drawn against the rack's standing bounds.
 */
export const BoardRotatedGroup = ({
  box,
  children,
  className,
  pivotXMillimeters,
  pivotYMillimeters,
  rotationDegrees,
}: {
  box: BoardBox;
  children: ReactNode;
  className?: string;
  pivotXMillimeters: number;
  pivotYMillimeters: number;
  rotationDegrees: number;
}) => (
  <div
    className={cn("absolute", className)}
    style={{
      left: `${box.xMillimeters}px`,
      top: `${box.yMillimeters}px`,
      width: `${box.widthMillimeters}px`,
      height: `${box.heightMillimeters}px`,
      transform: `rotate(${rotationDegrees}deg)`,
      transformOrigin: `${pivotXMillimeters}px ${pivotYMillimeters}px`,
    }}
  >
    {children}
  </div>
);

/**
 * What a rack is made of, as CSS custom properties the rack's own faces read.
 *
 * One colour is given and every surface is derived from it here, in the theme
 * the page is actually being drawn in: the same colour has to hold its edges
 * against a light background and a dark one, which is a question `color-mix`
 * answers and arithmetic in JavaScript cannot.
 *
 * Flat and square, and derived exactly as the desktop derives it: a body, a
 * hairline edge, and the bays the discs stand in. `--shelf-bay` is one
 * compartment — lighter than the gaps between bays, so those gaps are the
 * dividers — and it keeps the rack's own hue rather than heading for black, or
 * every finish would become the same black rectangle. A near-black finish has
 * nothing darker to cut, so its inside is drawn at fixed lightnesses instead.
 *
 * `--shelf-ink` is the one thing here that is **not** derived in CSS, because
 * it is a question about the rack's own colour rather than about the theme
 * around it — see `resolveShelfInkTone`.
 */
export const ShelfSurface = ({
  children,
  color,
  inkTone,
  isNearBlack,
}: {
  children: ReactNode;
  color: string;
  inkTone: "dark" | "light";
  /** See `isShelfFinishNearBlack`. */
  isNearBlack: boolean;
}) => (
  <div
    className="contents"
    style={
      (isNearBlack
        ? {
            "--shelf-body": color,
            "--shelf-edge": `oklch(from ${color} 0.34 c h)`,
            "--shelf-bay": `oklch(from ${color} 0.2 c h)`,
            "--shelf-divider": `oklch(from ${color} 0.27 c h)`,
            "--shelf-case": `oklch(from ${color} 0.14 c h)`,
            "--shelf-ink": "#ffffff",
          }
        : {
            "--shelf-body": color,
            "--shelf-edge": `color-mix(in oklab, ${color} 55%, black)`,
            "--shelf-bay": `color-mix(in oklab, ${color} 40%, black)`,
            "--shelf-divider": `color-mix(in oklab, ${color} 70%, black)`,
            "--shelf-case": `color-mix(in oklab, ${color} 22%, black)`,
            "--shelf-ink": inkTone === "dark" ? "#101218" : "#ffffff",
          }) as CSSProperties
    }
  >
    {children}
  </div>
);

/**
 * The grid the racks stand on, moving and scaling with the board.
 *
 * Painted as a background, which is the cheap way to draw an endless grid —
 * but a background stays where the window put it unless it is told otherwise,
 * so racks panned across a still grid looked like they were sliding over the
 * board rather than the board moving. Its tile and its origin are the board's
 * zoom and pan, which is why this is a leaf with a `style` of its own.
 */
export const BoardPatternSurface = ({
  pattern,
  view,
}: {
  pattern: BoardPattern;
  view: ShelfBoardView;
}) => {
  const { backgroundPosition, backgroundSize, dotRadiusPixels } = resolveBoardPatternGeometry({
    originXPixels: view.panOffsetXPixels,
    originYPixels: view.panOffsetYPixels,
    pattern,
    zoomPercentage: view.zoomPercentage,
  });
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0",
        pattern === "dots" ? "board-pattern-dots" : "board-pattern-lines",
      )}
      style={
        {
          backgroundPosition,
          backgroundSize,
          ...(dotRadiusPixels === undefined
            ? {}
            : { "--board-pattern-dot-radius": `${dotRadiusPixels}px` }),
        } as CSSProperties
      }
    />
  );
};
